import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useProduct } from '@data/hooks/products';
import { Loader, LoaderSize } from '@ui';

import TeamAndRatesTab from '../../products/productDetail/TeamAndRatesTab';
import { UnsavedWorkProvider, useUnsavedWork } from '../../products/productDetail/unsavedWork';
import { useProductStore } from '../../products/productDetail/useProductStore';
import type { PricingGaps } from './pricingGaps';

type Props = {
  organizationId: string;
  productId: string;
  /** Opens on the rate version this sprint is priced under. */
  sprintStart?: string;
  /** Who the send step could not price, and where: marked in the team and the matrix. */
  gaps: PricingGaps;
  onBack: () => void;
};

const Panel = ({ organizationId, productId, sprintStart, gaps, onBack }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { guard } = useUnsavedWork();
  // Writable: the send step reads these figures read-only, and a read-only load drops every save.
  const store = useProductStore(productId);
  const { data: product } = useProduct(productId);
  const back = (): void => guard(onBack);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="mb-3 flex items-start gap-2">
        <button
          type="button"
          onClick={back}
          aria-label={t('incidentReports.backToReport')}
          className="-ml-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <ChevronLeft className="size-4" aria-hidden />
        </button>
        <div>
          <h2 className="text-base font-medium text-foreground">{t('mailPreview.pricingTitle')}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{t('mailPreview.pricingHint')}</p>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-2">
        {store.isReady && product ? (
          <TeamAndRatesTab
            key={store.revision}
            product={product}
            organizationId={organizationId}
            canEdit
            openOn={sprintStart}
            pricing={{
              highlight: gaps.unlevelled.map(worker => worker.id),
              chapters: gaps.chapters,
              workers: gaps.workers,
              missingRates: gaps.missingRates,
            }}
          />
        ) : (
          <div className="flex justify-center py-10">
            <Loader size={LoaderSize.medium} />
          </div>
        )}
      </div>
    </div>
  );
};

/** The product's team and cost matrix inside the send step, so a missing level or rate is filled in without leaving it. */
const SprintPricingPanel = (props: Props): JSX.Element => (
  <UnsavedWorkProvider>
    <Panel {...props} />
  </UnsavedWorkProvider>
);

export default SprintPricingPanel;
